import { EditorTab } from "./_EditorTab";
import { DiffViewTab } from "./_DiffViewTab";
import { MonacoMarkdownEditor } from "./_MonacoMarkdownEditor";

import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";

import { Typography, AppButton } from "@libs/zapi-ui/custom";
import { CardHeader, Switch } from "@libs/zapi-ui/shadcn";

import { CopyIcon, ClipboardTextIcon } from "@phosphor-icons/react";

import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { hasPendingDiffs } from "@/zapi-frontend/utils/diff-utils";
import { toast } from "sonner";
import { ProjectDescription } from "./_ProjectDescription";

export function MemoIsland() {
  const { activeChapter } = useChapterStore();
  const { isLabs } = useMeStore();

  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const chapterId = activeChapter?.id?.toString() || "";

  const { selectedProject, isFetchProjectLoading } = useProjectStore();

  const { getDiffEditor } = useDiffStore();
  const htmlContent = getDiffEditor(projectId, chapterId)?.htmlContent;
  const hasDiff = !!htmlContent && hasPendingDiffs(htmlContent);

  const { getMarkdown, setContent } = useEditorStore();

  const [isRawView, setIsRawView] = useState(false);

  useEffect(() => {
    if (!isLabs && isRawView) {
      setIsRawView(false);
    }
  }, [isLabs, isRawView]);

  const handleCopyMarkdown = async () => {
    try {
      let markdown: string | null = null;

      if (isRawView) {
        // In raw view, get content directly from chapter store
        const { getChapterContent } = useChapterStore.getState();
        markdown = getChapterContent(chapterId);
      } else {
        // In formatted view, get content from TipTap editor
        markdown = getMarkdown?.() || null;
      }

      if (!markdown) {
        toast.error("No content to copy");
        return;
      }
      await navigator.clipboard.writeText(markdown);
      toast.success("Markdown copied to clipboard");
    } catch (error) {
      console.error("Failed to copy markdown:", error);
      toast.error("Failed to copy markdown");
    }
  };

  const handlePasteMarkdown = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (!clipboardText.trim()) {
        toast.error("No content in clipboard");
        return;
      }

      if (isRawView) {
        // In raw view, update chapter content directly
        const { updateChapterContent } = useChapterStore.getState();
        updateChapterContent(chapterId, clipboardText, true);
      } else {
        // In formatted view, use TipTap editor
        if (!setContent) {
          toast.error("Editor not ready");
          return;
        }
        setContent(clipboardText);
      }

      toast.success("Markdown pasted successfully");
    } catch (error) {
      console.error("Failed to paste markdown:", error);
      toast.error("Failed to paste markdown");
    }
  };

  return (
    <div className="flex w-full max-w-[720px] h-full mx-auto">
      <div className="w-full flex flex-col bg-card h-full px-4 laptop:px-0">
        {isFetchProjectLoading && (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            <div className="h-4 w-4 border-t-2 border-blue-500 border-solid rounded-full animate-spin mr-2"></div>
            Loading memo...
          </div>
        )}

        {!isFetchProjectLoading && (
          <>
            {activeChapter && (
              <CardHeader
                className={cn(
                  "flex flex-col hd:flex-row hd:items-center gap-4 px-0 pt-5 laptop:pb-2 border-b border-innoveraZinc-150",
                  activeChapter?.projectTemplateChapter?.isExecutiveSummary && "border-transparent"
                )}
              >
                <div className="grid grid-cols-[1fr] flex-1">
                  <Typography variant="heading5" className="truncate">
                    {selectedProject?.name}
                  </Typography>
                  <ProjectDescription description={selectedProject?.shortDescription || ""} />
                </div>

                <div className="flex items-center gap-3">
                  {isLabs && (
                    <div className="flex items-center gap-3">
                      {!isRawView && (
                        <div className="flex items-center gap-1">
                          <AppButton
                            appearance="text"
                            size="sm"
                            onClick={handleCopyMarkdown}
                            disabled={isFetchProjectLoading}
                            className="h-8 px-2 text-sm"
                            title="Copy Markdown"
                            startAdornment={<CopyIcon className="h-4 w-4" />}
                          />

                          <AppButton
                            appearance="text"
                            size="sm"
                            onClick={handlePasteMarkdown}
                            disabled={isFetchProjectLoading}
                            className="h-8 px-2 text-sm"
                            title="Paste Markdown"
                            startAdornment={<ClipboardTextIcon className="h-4 w-4" />}
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-sm">
                        <span className={cn("transition-colors", isRawView ? "text-gray-500" : "text-gray-900")}>
                          Formatted
                        </span>
                        <Switch
                          checked={isRawView}
                          onCheckedChange={setIsRawView}
                          disabled={isFetchProjectLoading}
                          className="h-5 w-8"
                        />
                        <span className={cn("transition-colors", isRawView ? "text-gray-900" : "text-gray-500")}>
                          Raw
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
            )}
            <div className="p-0 m-0 flex-1 flex flex-col relative" data-testid="memo-editor-container">
              {/* TipTap Editor - Always rendered, visibility controlled by CSS */}
              <div className={isRawView ? "hidden" : "block h-full"}>
                <EditorTab
                  projectId={projectId}
                  isLoading={isFetchProjectLoading}
                  isEditable={true}
                  className={!hasDiff ? "swap-anim" : "swap-hidden"}
                />
                <DiffViewTab className={hasDiff ? "swap-anim" : "swap-hidden"} />
              </div>

              {/* Monaco Editor - Always rendered, visibility controlled by CSS */}
              <div className={isRawView ? "block h-full" : "hidden"}>
                <div className="p-1 pt-0 flex-1 flex flex-col h-full">
                  {isFetchProjectLoading ? (
                    <div className="flex items-center justify-center h-48 text-sm text-gray-500">
                      <div className="h-4 w-4 border-t-2 border-blue-500 border-solid rounded-full animate-spin mr-2"></div>
                      Loading memo...
                    </div>
                  ) : (
                    <MonacoMarkdownEditor projectId={projectId} className="flex-1" />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
